import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2,
  Download,
  Calendar,
  Users,
  MapPin,
  Hotel,
  Plane,
  Ticket,
  Car,
  Clock,
  Star,
  ChevronDown,
  FileText,
  Package,
  CheckCircle,
  AlertCircle,
  XCircle,
  Phone,
  StickyNote,
} from "lucide-react";
import hcteLogo from "@/assets/hcte-logo.svg";
import { format } from "date-fns";
import { downloadPublicBookingServiceTicketFile, getPublicBookingPackage } from "@/lib/bookingPackagesApi";

type ReservationStatus = "pending_decision" | "needs_reservation" | "reservation_attached" | "no_reservation_required";

interface BookingServiceTicketFile {
  id: string;
  file_name: string | null;
  uploaded_at: string | null;
  download_url: string | null;
}

interface BookingService {
  id: string;
  service_type: string;
  service_name: string;
  service_date: string | null;
  service_time: string | null;
  service_description: string | null;
  ticket_type: string | null;
  quantity: number | null;
  public_note: string | null;
  no_ticket_required: boolean;
  ticket_file_name: string | null;
  service_data: any;
  reservation_status: ReservationStatus;
  ticket_download_url: string | null;
  ticket_files: BookingServiceTicketFile[] | null;
}

interface BookingPackageData {
  id: string;
  proposal_id: string;
  status: string;
  created_at: string;
  guests?: string | null;
  hotel?: string | null;
  contact?: string | null;
}

interface ProposalData {
  id: string;
  title: string;
  main_destination: string;
  pax_adults: number;
  pax_children: number;
  pax_infants: number;
  clients: {
    first_name: string;
    last_name: string;
    email: string;
  } | null;
}

const PublicBookingPackage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [bookingPackage, setBookingPackage] = useState<BookingPackageData | null>(null);
  const [proposal, setProposal] = useState<ProposalData | null>(null);
  const [services, setServices] = useState<BookingService[]>([]);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (id) {
      fetchBookingData();
    }
  }, [id]);

  const fetchBookingData = async () => {
    try {
      setLoading(true);
      if (!id) return;
      const data = await getPublicBookingPackage(id);
      setBookingPackage({
        id: data.id,
        proposal_id: data.proposal_id,
        status: data.status,
        created_at: data.created_at,
        guests: data.guests ?? null,
        hotel: data.hotel ?? null,
        contact: data.contact ?? null,
      });
      setProposal(data.proposal || null);
      setServices(
        (data.services || [])
          .filter((s) => (s?.service_type || "").toLowerCase() !== "handling_fee")
          .map((s) => ({
            ...s,
            reservation_status: s.reservation_status as ReservationStatus,
            ticket_files: s.ticket_files ?? [],
          }))
      );
    } catch (error: any) {
      toast({
        title: "Error loading booking",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReservation = async (service: BookingService, ticketFile: BookingServiceTicketFile) => {
    if (!bookingPackage) return;

    try {
      setDownloadingId(`${service.id}:${ticketFile.id}`);
      const { blob, fileName } = await downloadPublicBookingServiceTicketFile(bookingPackage.id, service.id, ticketFile.id);
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = fileName || ticketFile.file_name || "reservation.pdf";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error: any) {
      toast({
        title: "Download failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const groupServicesByDate = useMemo(() => {
    const groups: Record<string, BookingService[]> = {};
    
    services.forEach((service) => {
      const dateKey = service.service_date || "no-date";
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(service);
    });

    Object.keys(groups).forEach((key) => {
      groups[key].sort((a, b) => {
        if (!a.service_time && !b.service_time) return 0;
        if (!a.service_time) return 1;
        if (!b.service_time) return -1;
        return a.service_time.localeCompare(b.service_time);
      });
    });

    return groups;
  }, [services]);

  const sortedDates = useMemo(() => {
    return Object.keys(groupServicesByDate).sort((a, b) => {
      if (a === "no-date") return 1;
      if (b === "no-date") return -1;
      return new Date(a).getTime() - new Date(b).getTime();
    });
  }, [groupServicesByDate]);

  const getServiceIcon = (type: string) => {
    switch (type) {
      case "tour":
        return <Ticket className="h-5 w-5" />;
      case "attraction":
        return <Star className="h-5 w-5" />;
      case "transfer":
        return <Car className="h-5 w-5" />;
      case "airport_service":
        return <Plane className="h-5 w-5" />;
      case "accommodation":
        return <Hotel className="h-5 w-5" />;
      case "flight":
        return <Plane className="h-5 w-5" />;
      default:
        return <Package className="h-5 w-5" />;
    }
  };

  const getServiceTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      tour: "Tour",
      attraction: "Attraction",
      transfer: "Transfer",
      airport_service: "Airport Service",
      accommodation: "Accommodation",
      additional_request: "Additional Service",
      flight: "Flight",
    };
    return labels[type] || type;
  };

  const renderReservationStatus = (service: BookingService) => {
    const ticketFiles = service.ticket_files ?? [];
    if (service.reservation_status === "reservation_attached" && ticketFiles.length > 0) {
      return (
        <div className="flex flex-col items-stretch gap-2 md:items-end">
          {ticketFiles.map((ticketFile, index) => {
            const key = `${service.id}:${ticketFile.id}`;
            return (
              <Button
                key={ticketFile.id}
                onClick={() => handleDownloadReservation(service, ticketFile)}
                disabled={downloadingId === key}
                className="bg-emerald-600 hover:bg-emerald-700 justify-start"
                title={ticketFile.file_name ?? undefined}
              >
                {downloadingId === key ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {ticketFiles.length > 1 ? `Download Reservation ${index + 1}` : "Download Reservation"}
              </Button>
            );
          })}
        </div>
      );
    }

    if (service.reservation_status === "no_reservation_required") {
      return (
        <div className="flex items-center gap-2 text-muted-foreground">
          <CheckCircle className="h-4 w-4 text-slate-500" />
          <span className="text-sm">No reservation required for this service</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2 text-amber-600">
        <AlertCircle className="h-4 w-4" />
        <span className="text-sm">Reservation not available yet</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!bookingPackage || !proposal) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100">
        <div className="text-center">
          <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground">Booking Not Found</h1>
          <p className="text-muted-foreground mt-2">
            This booking package may not exist or is not available.
          </p>
        </div>
      </div>
    );
  }

  const totalPax = (proposal.pax_adults || 0) + (proposal.pax_children || 0) + (proposal.pax_infants || 0);

  const completedCount = services.filter(
    (s) => s.reservation_status === "reservation_attached" || s.reservation_status === "no_reservation_required"
  ).length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      <header className="bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src={hcteLogo} alt="HCTE Logo" className="h-10" />
              <div className="hidden sm:block">
                <h1 className="text-lg font-semibold text-primary">HCTE</h1>
                <p className="text-xs text-muted-foreground">highclass travel experiences</p>
              </div>
            </div>
            <Badge variant="outline" className="text-sm px-3 py-1">
              <Package className="h-4 w-4 mr-1" />
              Booking Confirmed
            </Badge>
          </div>
        </div>
      </header>

      <div className="bg-gradient-to-r from-primary/10 to-primary/5 py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
            {proposal.title}
          </h1>
          <div className="flex flex-wrap gap-4 mt-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-5 w-5" />
              <span>{proposal.main_destination}</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Users className="h-5 w-5" />
              <span>{totalPax} Passenger{totalPax !== 1 ? "s" : ""}</span>
            </div>
          </div>
          {proposal.clients && (
            <p className="mt-4 text-muted-foreground">
              Welcome, {proposal.clients.first_name} {proposal.clients.last_name}
            </p>
          )}
          {(bookingPackage.guests || bookingPackage.hotel || bookingPackage.contact) && (
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              {bookingPackage.guests && (
                <div className="flex items-center gap-2 rounded-lg border bg-white/80 px-3 py-2 shadow-sm">
                  <Users className="h-4 w-4 text-primary" />
                  <div className="leading-tight">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Guests</p>
                    <p className="text-sm font-medium text-foreground">{bookingPackage.guests}</p>
                  </div>
                </div>
              )}
              {bookingPackage.hotel && (
                <div className="flex items-center gap-2 rounded-lg border bg-white/80 px-3 py-2 shadow-sm">
                  <Hotel className="h-4 w-4 text-primary" />
                  <div className="leading-tight">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Hotel</p>
                    <p className="text-sm font-medium text-foreground">{bookingPackage.hotel}</p>
                  </div>
                </div>
              )}
              {bookingPackage.contact && (
                <div className="flex items-center gap-2 rounded-lg border bg-white/80 px-3 py-2 shadow-sm">
                  <Phone className="h-4 w-4 text-primary" />
                  <div className="leading-tight">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Contact</p>
                    <p className="text-sm font-medium text-foreground">{bookingPackage.contact}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-emerald-100">
                  <CheckCircle className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Your Booking is Confirmed</h2>
                  <p className="text-muted-foreground mt-1">
                    Below you'll find all your booked services. Download your reservations when available.
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {completedCount} of {services.length} services ready
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {sortedDates.map((dateKey) => (
            <Collapsible key={dateKey} defaultOpen>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-primary" />
                        {dateKey === "no-date"
                          ? "Services (Date TBD)"
                          : format(new Date(dateKey), "EEEE, MMMM d, yyyy")}
                      </div>
                      <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform duration-200" />
                    </CardTitle>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0">
                    <div className="space-y-4">
                      {groupServicesByDate[dateKey].map((service) => (
                        <div
                          key={service.id}
                          className={`border rounded-lg p-4 shadow-sm transition-colors ${
                            service.reservation_status === "reservation_attached"
                              ? "bg-emerald-50/50 border-emerald-200"
                              : service.reservation_status === "no_reservation_required"
                              ? "bg-white border-slate-200"
                              : "bg-white border-amber-200"
                          }`}
                        >
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                                  {getServiceIcon(service.service_type)}
                                </div>
                                <Badge variant="outline">
                                  {getServiceTypeLabel(service.service_type)}
                                </Badge>
                                {service.service_time && (
                                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {service.service_time.slice(0, 5)}
                                  </span>
                                )}
                              </div>
                              <h3 className="font-semibold text-lg">{service.service_name}</h3>
                              {service.service_description && (
                                <p className="text-sm text-muted-foreground mt-2 line-clamp-3">
                                  {service.service_description}
                                </p>
                              )}
                              {(service.ticket_type || service.quantity != null) && (
                                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                                  {service.ticket_type && (
                                    <span>
                                      Ticket type: <span className="font-medium text-foreground">{service.ticket_type}</span>
                                    </span>
                                  )}
                                  {service.quantity != null && (
                                    <span>
                                      Qty: <span className="font-medium text-foreground">{service.quantity}</span>
                                    </span>
                                  )}
                                </div>
                              )}
                              {service.public_note && (
                                <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/70 p-3">
                                  <StickyNote className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                                  <p className="text-sm text-amber-900 whitespace-pre-wrap">{service.public_note}</p>
                                </div>
                              )}
                            </div>
                            <div className="flex-shrink-0">
                              {renderReservationStatus(service)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          ))}

          {services.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No services in this booking yet.</p>
              </CardContent>
            </Card>
          )}

          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6">
              <div className="text-center">
                <h3 className="font-semibold">Need Help?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Contact us at support@hcte.com or call +971 XXX XXX XXXX
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <footer className="bg-white border-t py-6 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} HCTE - High Class Travel Experiences. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PublicBookingPackage;
